/* Kind-portfolio — chronologisch jaaroverzicht van schriftjes + foto's per kind.
 * Print-vriendelijk: ouders kunnen dit als kalenderjaar-portfolio printen. */
import { useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ChevronLeft, Printer, BookOpen, Camera, Baby, FileDown } from "lucide-react";
import { toast } from "sonner";

const photoUrl = (url) => (url?.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url);

export default function PortaalPortfolioPage() {
  const { user, status } = useAuth();
  const navigate = useNavigate();
  const [kinderen, setKinderen] = useState([]);
  const [selectedKid, setSelectedKid] = useState(null);
  const [data, setData] = useState(null);
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);

  useEffect(() => {
    if (status !== "authed" || user?.role !== "ouder") return;
    api.get("/portaal/overview")
      .then(r => {
        setKinderen(r.data.kinderen || []);
        if ((r.data.kinderen || []).length) setSelectedKid(r.data.kinderen[0]);
      })
      .catch(e => toast.error(formatApiError(e.response?.data?.detail)));
  }, [status, user]);

  useEffect(() => {
    if (!selectedKid) return;
    api.get(`/portaal/kind/${selectedKid.id}/portfolio`, {
      params: { date_from: `${year}-01-01`, date_to: `${year}-12-31` },
    })
      .then(r => setData(r.data))
      .catch(() => setData(null));
  }, [selectedKid, year]);

  // Group timeline per month
  const grouped = useMemo(() => {
    const g = {};
    (data?.timeline || []).forEach(it => {
      const ym = (it.datum || "").slice(0, 7);
      (g[ym] ||= []).push(it);
    });
    return Object.entries(g).sort((a, b) => a[0].localeCompare(b[0]));
  }, [data]);

  if (status === "loading") return <div className="min-h-screen flex items-center justify-center bg-[color:var(--bone)]">Laden…</div>;
  if (status === "anon") return <Navigate to="/login" replace />;
  if (user?.role !== "ouder") return <Navigate to="/app" replace />;

  return (
    <div className="min-h-screen bg-[color:var(--bone)] subtle-grain" data-testid="portaal-portfolio-page">
      <header className="bg-white border-b border-neutral-200 sticky top-0 z-10 print:hidden">
        <div className="max-w-3xl mx-auto px-4 h-14 flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate("/portaal")} data-testid="portfolio-back-btn">
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <div className="w-8 h-8 rounded-md flex items-center justify-center" style={{ background: "var(--sage)" }}>
            <BookOpen className="h-4 w-4 text-white" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-display text-sm leading-tight truncate">Portfolio {selectedKid?.voornaam ? `— ${selectedKid.voornaam}` : ""}</div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500">Jaaroverzicht {year}</div>
          </div>
          <Button variant="outline" size="sm" onClick={() => window.print()} data-testid="portfolio-print-btn">
            <Printer className="h-4 w-4 mr-1" /> Print
          </Button>
          <Button
            size="sm"
            onClick={() => {
              if (!selectedKid) return;
              const url = `${process.env.REACT_APP_BACKEND_URL}/api/portaal/kind/${selectedKid.id}/portfolio.pdf?year=${year}`;
              window.open(url, "_blank");
            }}
            data-testid="portfolio-pdf-btn"
            className="text-white"
            style={{ background: "linear-gradient(135deg,#a68bd8,#8065c0)" }}
          >
            <FileDown className="h-4 w-4 mr-1" /> Download PDF
          </Button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-4 pb-20 space-y-4 print:py-2">
        {kinderen.length > 1 && (
          <div className="flex gap-2 overflow-x-auto pb-1 print:hidden" data-testid="portfolio-kind-picker">
            {kinderen.map(k => (
              <button
                key={k.id}
                onClick={() => setSelectedKid(k)}
                data-testid={`portfolio-kid-${k.id}`}
                className={`shrink-0 px-3 py-1.5 rounded-full border text-sm ${selectedKid?.id === k.id ? "bg-[color:var(--sage)] text-white border-[color:var(--sage)]" : "border-neutral-200 bg-white"}`}
              >
                {k.voornaam}
              </button>
            ))}
          </div>
        )}

        <Card className="p-4 bg-white shadow-none flex items-center justify-between print:shadow-none print:border-0" data-testid="portfolio-header">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500">Portfolio</div>
            <div className="font-display text-2xl text-neutral-800">
              {selectedKid ? `${selectedKid.voornaam} ${selectedKid.achternaam}` : "—"}
            </div>
            {data && (
              <div className="text-xs text-neutral-500 mt-1">
                {data.totals.schriftjes} schriftjes · {data.totals.photos} foto's in {year}
              </div>
            )}
          </div>
          <div className="flex gap-1 print:hidden">
            {[currentYear - 1, currentYear].map(y => (
              <button key={y} onClick={() => setYear(y)} data-testid={`portfolio-year-${y}`}
                className={`px-3 py-1 rounded-md text-xs font-medium ${year === y ? "bg-[color:var(--sage)] text-white" : "bg-neutral-100"}`}>{y}</button>
            ))}
          </div>
        </Card>

        {grouped.length === 0 && (
          <Card className="p-6 bg-white text-center" data-testid="portfolio-empty">
            <Baby className="h-8 w-8 text-neutral-300 mx-auto mb-2" />
            <div className="text-sm text-neutral-500">Nog geen portfolio-items voor {year}.</div>
          </Card>
        )}

        {grouped.map(([ym, entries]) => (
          <div key={ym} data-testid={`portfolio-month-${ym}`} className="print:break-inside-avoid">
            <div className="text-xs uppercase tracking-widest text-neutral-500 font-semibold mb-2 sticky top-14 bg-[color:var(--bone)] py-1 print:static">
              {new Date(ym + "-01").toLocaleDateString("nl-NL", { month: "long", year: "numeric" })}
              <span className="ml-2 normal-case tracking-normal font-normal text-neutral-400">{entries.length} items</span>
            </div>
            <div className="space-y-2">
              {entries.map((it, i) => (
                <Card key={i} className="p-3 bg-white shadow-none flex gap-3 print:shadow-none" data-testid={`portfolio-item-${ym}-${i}`}>
                  {it.type === "photo" ? (
                    <img src={photoUrl(it.data.url)} alt="" className="w-20 h-20 rounded object-cover shrink-0" />
                  ) : (
                    <div className="w-20 h-20 rounded bg-[color:var(--sage-100)] flex items-center justify-center text-[color:var(--sage)] shrink-0">
                      <BookOpen className="h-8 w-8" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="text-[10px] text-neutral-500 uppercase tracking-widest">
                        {new Date(it.datum).toLocaleDateString("nl-NL", { weekday: "short", day: "numeric", month: "short" })}
                      </span>
                      {it.type === "schriftje" ? (
                        <span className="text-[10px] bg-[color:var(--sage-100)] text-[color:var(--sage)] px-1.5 rounded">Schriftje</span>
                      ) : (
                        <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 rounded flex items-center gap-0.5"><Camera className="h-2.5 w-2.5" /> Foto</span>
                      )}
                    </div>
                    {it.type === "schriftje" ? (
                      <>
                        {it.data.hoogtepunt && <div className="text-sm font-medium text-neutral-800">{it.data.hoogtepunt}</div>}
                        {it.data.activiteiten && <div className="text-xs text-neutral-600 whitespace-pre-line line-clamp-3">{it.data.activiteiten}</div>}
                      </>
                    ) : (
                      it.data.caption && <div className="text-sm text-neutral-700">{it.data.caption}</div>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          </div>
        ))}
      </main>
    </div>
  );
}
